import { Box, Heading, Text } from '@chakra-ui/core';

import React from 'react';
import { Cell } from 'react-table';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { FormattedTodo, TodoActionType } from '../../types';
import LanguageEvaluationStatusIcon from '../../../../assets/img/language-evaluation-icon.svg';
import ConversationalAiIcon from '../../../../assets/img/conversational-ai-icon.svg';
import { useStoreState } from '../../../../models/hooks';
import { getTranslatedValue } from '../../../../utils/localizedString';
import { TodosType } from '../../../../firebase/firestore/documents/lookups';
import { getConsistentDateFormat } from '../../../../utils/dateUtils';

interface LastUpdatesProps {
  cellData: Cell<FormattedTodo, any>;
  candidateTodos: TodosType | undefined;
}

interface StatusBoxProps {
  hours: number;
  bgColor: string;
  t: (key: string) => string;
}

const StatusBox: React.FC<StatusBoxProps> = React.memo(function StatusBox({ hours, bgColor, t }) {
  return (
    <Box
      as="span"
      d="inline-block"
      whiteSpace="nowrap"
      px="6px"
      py="3px"
      ml={2}
      fontSize="13px"
      borderRadius="46px"
      lineHeight={1.1}
      bg={bgColor}
      color="#fff"
    >
      {`${hours} ${t('toDoList:hrPlus')}`}
    </Box>
  );
});

const getDataOfHoursAgo = (status: string, t: (key: string) => string) => {
  /* istanbul ignore next */
  switch (status) {
    case 'hireFormRequested12HoursAgo':
    case 'offerMade12HoursAgo':
    case 'offerRejected12HoursAgo':
    case 'offerExpired12HoursAgo':
    case 'hireFormResubmission12HoursAgo':
    case 'offerAccepted12HoursAgo':
      return <StatusBox hours={12} bgColor="#2196F3" t={t} />;
    case 'hireFormCompleted24HoursAgo':
    case 'hireFormRequested24HoursAgo':
    case 'hireFormResubmission24HoursAgo':
    case 'bgCheckRequested24HoursAgo':
    case 'offerMade24HoursAgo':
    case 'offerRejected24HoursAgo':
    case 'offerExpired24HoursAgo':
    case 'workbrightRequested24HoursAgo':
    case 'workbrightRestarted24HoursAgo':
    case 'bgCheckNotProcessedByCertn24HoursAgoCandidates':
    case 'offerAccepted24HoursAgo':
      return <StatusBox hours={24} bgColor="#D69E2E" t={t} />;
    case 'offerMade36HoursAgo':
    case 'offerRejected36HoursAgo':
    case 'offerExpired36HoursAgo':
    case 'hireFormResubmission36HoursAgo':
    case 'workbrightRequested36HoursAgo':
    case 'workbrightRestarted36HoursAgo':
    case 'offerAccepted36HoursAgo':
      return <StatusBox hours={36} bgColor="#D69E2E" t={t} />;
    case 'bgCheckRequested48HoursAgo':
    case 'offerMade48HoursAgo':
    case 'offerRejected48HoursAgo':
    case 'offerExpired48HoursAgo':
    case 'hireFormRequested48HoursAgo':
    case 'hireFormResubmission48HoursAgo':
    case 'workbrightRequested48HoursAgo':
    case 'workbrightRestarted48HoursAgo':
    case 'offerAccepted48HoursAgo':
      return <StatusBox hours={48} bgColor="#E53E3E" t={t} />;
    case 'workbrightRequested60HoursAgo':
    case 'workbrightRestarted60HoursAgo':
    case 'offerAccepted60HoursAgo':
      return <StatusBox hours={60} bgColor="#E53E3E" t={t} />;
    case 'hireFormRequested72HoursAgo':
    case 'bgCheckRequested72HoursAgo':
    case 'hireFormResubmission72HoursAgo':
    case 'workbrightRequested72HoursAgo':
    case 'workbrightRestarted72HoursAgo':
    case 'offerAccepted72HoursAgo':
      return <StatusBox hours={72} bgColor="#E53E3E" t={t} />;
    default:
      return <Box as="span" />;
  }
};

const getColumnActionLabel = (
  title: string,
  isRequisitionEnabled: boolean | undefined,
  candidateTodos: TodosType | undefined,
) => {
  const actionLabelObject =
    !isRequisitionEnabled && title === 'hireFormCompleted24HoursAgo'
      ? candidateTodos?.[title as keyof TodosType]?.general?.actionLabel
      : candidateTodos?.[title as keyof TodosType]?.actionLabel;
  return actionLabelObject ? getTranslatedValue(actionLabelObject, i18next.language) : title;
};

export const LastUpdate = React.memo(function LastUpdate({ cellData, candidateTodos }: LastUpdatesProps) {
  const columnData = cellData.row.original;
  const { t } = useTranslation();
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);

  return (
    <Box className={cellData.column.id} data-testid={cellData.column.id}>
      <Text fontSize="15px" color="gray.600" mb={1} d="flex" alignItems="center">
        {columnData.actionType === TodoActionType.LANGUAGE_EVALUATION && (
          <Box mr="5px" display="inline-block" verticalAlign="middle" mb="5px">
            <img src={LanguageEvaluationStatusIcon} alt="language evaluation" height={20} width={20} />
          </Box>
        )}
        {columnData.actionType === TodoActionType.CONVERSATIONAL_AI && (
          <Box mr="5px" display="inline-block" verticalAlign="middle" mb="5px">
            <img src={ConversationalAiIcon} alt="conversational ai" height={20} width={20} />
          </Box>
        )}
        {getConsistentDateFormat(columnData.lastStatus, 'full', 'hh:mm A')}

        {getDataOfHoursAgo(columnData.title, t)}
      </Text>
      <Heading fontSize="16px" color="gray.700" mb={1} lineHeight="1.2">
        {getColumnActionLabel(columnData.title, isRequisitionEnabled, candidateTodos)}
      </Heading>
    </Box>
  );
});
